import { useLiveQuery } from '@electric-sql/pglite-react';
import { BrandSettings, backgroundSettings, emailProblem } from '@quickreport/domain';
import {
  BrandDetails,
  BrandLook,
  BrandPage,
  BrandPrivacy,
  Button,
  detectLetterhead,
  prepareBackground,
  SignatoryManager,
  Tabs,
} from '@quickreport/ui';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWorkspace } from '../../lib/workspace';
import {
  ImageError,
  loadImage,
  type StoredImage,
  storeImage,
  storePrepared,
} from '../../local-db/files';
import {
  brandExists,
  loadBrand,
  removeSignatory,
  type SignatoryRow,
  saveBrand,
  saveSignatory,
} from '../../local-db/settings';
import { BrandPreview, type PreviewSignatory } from './BrandPreview';

type Tab = 'details' | 'look' | 'signatories' | 'page' | 'privacy';
export function SettingsPage() {
  const ws = useWorkspace();
  const canEdit = ws.can('manage_site');
  const [tab, setTab] = useState<Tab>(() => {
    // "#signatories" opens that tab (the "add a signing doctor" link uses it).
    const h = window.location.hash.replace('#', '');
    return (
      (['details', 'look', 'signatories', 'page', 'privacy'] as Tab[]).find((t) => t === h) ??
      'details'
    );
  });
  const [brand, setBrand] = useState<BrandSettings | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [logo, setLogo] = useState<StoredImage | null>(null);
  const [background, setBackground] = useState<StoredImage | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [pdf, setPdf] = useState<Uint8Array | null>(null);

  // A change saved on another device arrives by sync; it shows here unless this page has unsaved
  // edits (saving those then makes them the latest, everywhere).
  const stampRes = useLiveQuery<{ rev: string; updated_at: string }>(
    'select rev::text, updated_at::text from brand_settings where org_id = $1 and site_id is null',
    [ws.orgId],
  );
  const stamp = stampRes
    ? `${stampRes.rows[0]?.rev ?? ''}:${stampRes.rows[0]?.updated_at ?? ''}`
    : null;
  const editing = useRef(false);
  useEffect(() => {
    if (stamp === null || editing.current) return;
    let alive = true;
    Promise.all([
      loadBrand(ws.db, ws.orgId, { labName: ws.auth.orgName }),
      brandExists(ws.db, ws.orgId),
    ]).then(([b, hadRow]) => {
      if (!alive || editing.current) return;
      setBrand(b);
      setSaved(hadRow ? JSON.stringify(b) : '');
    });
    return () => {
      alive = false;
    };
  }, [ws.db, ws.orgId, ws.auth.orgName, stamp]);

  useEffect(() => {
    let alive = true;
    loadImage(ws.db, brand?.logoFileId).then((i) => alive && setLogo(i));
    return () => {
      alive = false;
    };
  }, [ws.db, brand?.logoFileId]);

  useEffect(() => {
    let alive = true;
    loadImage(ws.db, brand?.backgroundFileId).then((i) => alive && setBackground(i));
    return () => {
      alive = false;
    };
  }, [ws.db, brand?.backgroundFileId]);

  const sigRes = useLiveQuery<SignatoryRow>(
    'select id, name, qualification, designation, reg_no, signature_file_id, departments, member_id from signatories where org_id = $1 and deleted_at is null order by name',
    [ws.orgId],
  );
  const sigRows = useMemo(() => sigRes?.rows ?? [], [sigRes]);
  const [sigImages, setSigImages] = useState<Record<string, StoredImage | null>>({});
  useEffect(() => {
    let alive = true;
    (async () => {
      const next: Record<string, StoredImage | null> = {};
      for (const s of sigRows) next[s.id] = await loadImage(ws.db, s.signature_file_id);
      if (alive) setSigImages(next);
    })();
    return () => {
      alive = false;
    };
  }, [ws.db, sigRows]);

  const sigItems = useMemo(
    () =>
      sigRows.map((s) => ({
        id: s.id,
        name: s.name,
        qualification: s.qualification,
        designation: s.designation,
        regNo: s.reg_no,
      })),
    [sigRows],
  );
  const previewSigs: PreviewSignatory[] = useMemo(
    () =>
      sigRows.map((s) => ({
        id: s.id,
        name: s.name,
        qualification: s.qualification,
        designation: s.designation,
        regNo: s.reg_no,
        image: sigImages[s.id] ?? null,
      })),
    [sigRows, sigImages],
  );
  const onPdf = useCallback((p: Uint8Array | null) => setPdf(p), []);

  if (!brand) return <div className="p-8 text-slate">Opening settings…</div>;
  const dirty = JSON.stringify(brand) !== saved;
  editing.current = dirty && saved !== '';
  const set = (patch: Partial<BrandSettings>) => setBrand((b) => (b ? { ...b, ...patch } : b));

  async function save() {
    if (!brand) return;
    const mailErr = emailProblem(brand.email ?? '');
    if (mailErr) {
      setTab('details');
      setMessage({ tone: 'error', text: `Email: ${mailErr}` });
      return;
    }
    const parsed = BrandSettings.safeParse(brand);
    if (!parsed.success) {
      setMessage({
        tone: 'error',
        text: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join(' · '),
      });
      return;
    }
    await saveBrand(ws.db, ws.orgId, parsed.data);
    // Both as saved, so the page is not taken for still being edited (and keeps following sync).
    setBrand(parsed.data);
    setSaved(JSON.stringify(parsed.data));
    setMessage({
      tone: 'ok',
      text: 'Saved. New reports use these settings; reports already signed keep how they were printed.',
    });
  }

  async function pickBackground(file: File | undefined) {
    if (!brand) return;
    if (!file) {
      set({ backgroundFileId: undefined });
      return;
    }
    try {
      const prepared = await prepareBackground(file);
      const id = await storePrepared(ws.db, ws.orgId, prepared);
      set({ backgroundFileId: id, ...backgroundSettings(brand, prepared.fit) });
      setMessage({
        tone: 'ok',
        text: prepared.fit
          ? 'Letterhead set. Your own header is switched off and the space at the top and bottom is set from the picture. Check the sample, then Save.'
          : 'Letterhead set. Set the space at the top and bottom to suit it, then Save.',
      });
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ImageError ? err.message : 'Could not use that picture.',
      });
    }
  }

  async function fitBackground() {
    if (!brand || !background) return;
    const fit = await detectLetterhead(
      new Blob([background.bytes as BlobPart], { type: background.mime }),
    ).catch(() => null);
    set(backgroundSettings(brand, fit));
  }

  async function upload(file: File | undefined, what: 'logo') {
    if (!file) return;
    try {
      const id = await storeImage(ws.db, ws.orgId, file, { maxSide: 900 });
      if (what === 'logo') set({ logoFileId: id });
      setMessage(null);
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ImageError ? err.message : 'Could not use that picture.',
      });
    }
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-paper px-4 py-3 md:px-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold">Report Settings</h1>
          <p className="text-slate max-md:hidden">
            How your reports look. The paper is always white with black text; your colour is used
            only for the lab name and the header line.
          </p>
        </div>
        {pdf ? <SampleLink pdf={pdf} /> : null}
        <Button tone="primary" onClick={save} disabled={!dirty || !canEdit}>
          Save
        </Button>
      </header>
      {!canEdit ? (
        <div className="border-b border-line bg-canvas px-4 py-2 text-[13px] text-slate md:px-6">
          Only a lab admin or owner can change these settings.
        </div>
      ) : null}
      {message ? (
        <div
          className={`border-b px-4 py-2 text-[13px] md:px-6 ${message.tone === 'ok' ? 'border-green-200 bg-green-50 text-ok' : 'border-red-200 bg-red-50 text-flag'}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto p-4 md:p-6">
          <Tabs
            label="Settings sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'details', label: 'Lab details' },
              { value: 'look', label: 'Look' },
              { value: 'signatories', label: 'Signatories' },
              { value: 'page', label: 'Page & letterhead' },
              { value: 'privacy', label: 'Privacy' },
            ]}
          />
          <fieldset disabled={!canEdit} className="mt-5 grid max-w-2xl gap-5 border-0 p-0">
            {tab === 'details' ? <BrandDetails brand={brand} set={set} /> : null}
            {tab === 'look' ? (
              <BrandLook brand={brand} set={set} logo={logo} onLogo={(f) => upload(f, 'logo')} />
            ) : null}
            {tab === 'signatories' ? (
              <SignatoryManager
                items={sigItems}
                images={sigImages}
                canEdit={canEdit}
                onPickImage={async (file, opts) => {
                  const fileId = await storeImage(ws.db, ws.orgId, file, {
                    maxSide: 700,
                    whiteToTransparent: opts.whiteToTransparent,
                  });
                  const image = await loadImage(ws.db, fileId);
                  if (!image) throw new ImageError('Could not use that picture.');
                  return { fileId, image };
                }}
                onSave={async (v, existing) => {
                  const row = sigRows.find((r) => r.id === existing?.id);
                  await saveSignatory(ws.db, ws.orgId, {
                    ...(existing ? { id: existing.id } : {}),
                    name: v.name,
                    qualification: v.qualification,
                    designation: v.designation,
                    reg_no: v.regNo,
                    signature_file_id:
                      v.signatureFileId === undefined
                        ? (row?.signature_file_id ?? null)
                        : v.signatureFileId,
                    departments: row?.departments ?? [],
                    member_id: row?.member_id ?? null,
                  });
                }}
                onRemove={(s) => removeSignatory(ws.db, s.id)}
              />
            ) : null}
            {tab === 'page' ? (
              <BrandPage
                brand={brand}
                set={set}
                background={background}
                onBackground={(f) => void pickBackground(f)}
                onFitBackground={() => void fitBackground()}
              />
            ) : null}
            {tab === 'privacy' ? <BrandPrivacy brand={brand} set={set} /> : null}
          </fieldset>
        </div>
        <div className="hidden w-[24rem] shrink-0 border-l border-line lg:block xl:w-[30rem]">
          <BrandPreview
            brand={brand}
            logo={logo}
            background={background}
            signatories={previewSigs}
            onPdf={onPdf}
          />
        </div>
      </div>
    </div>
  );
}

function SampleLink({ pdf }: { pdf: Uint8Array }) {
  const url = useMemo(
    () => URL.createObjectURL(new Blob([pdf as BlobPart], { type: 'application/pdf' })),
    [pdf],
  );
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <a
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-paper px-3 font-medium hover:bg-canvas"
      href={url}
      download="quickreport-sample.pdf"
    >
      <Download size={15} /> Sample PDF
    </a>
  );
}
